import React, { useCallback, useState, useEffect, useMemo, memo } from "react";
import { Container, Graphics, Sprite, Text } from "@pixi/react";
import * as PIXI from "pixi.js";
import { PlayerBuildingState } from "../../types/buildings";

interface BuildingMarkerProps {
  x: number;
  y: number;
  buildingIcon?: string;
  scale?: number;
  onClick?: (event: PIXI.FederatedPointerEvent) => void;
  isHighlighted?: boolean;
  upgradeIcon?: string;
  buildingState?: PlayerBuildingState;
  fillPercentage?: number; // 0-100 percentage of fill level
  needsRepair?: boolean; // New prop to indicate if building needs repair
  repairIcon?: string; // Icon to show when building needs repair
  isBarracks?: boolean;
  needBuild?: boolean;
  buildIcon?: string;
  trainingQueue?: number; // Number of troops in training queue
  maxTrainingQueue?: number; // Maximum capacity of training queue
}

/**
 * BuildingMarker component - displays a circular badge with building icon
 * With animation effect on click - fills off/disappears
 * Shows fill level based on capacity percentage
 */

const BuildingMarker: React.FC<BuildingMarkerProps> = ({
  x,
  upgradeIcon,
  buildingState,
  y,
  isBarracks,
  buildingIcon,
  scale = 1,
  onClick,
  isHighlighted = false,
  fillPercentage = 0,
  needsRepair = false,
  repairIcon,
  needBuild,
  buildIcon,
  trainingQueue = 0,
  maxTrainingQueue = 0,
}) => {
  // Animation state
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationProgress, setAnimationProgress] = useState(0);
  const [visible, setVisible] = useState(true);
  const [pulsing, setPulsing] = useState(false);
  const [pulseScale, setPulseScale] = useState(1);

  // Calculate barracks fill percentage based on training queue, memoized
  const barracksQueuePercentage = useMemo(() => {
    if (isBarracks && maxTrainingQueue > 0) {
      return Math.min(100, (trainingQueue / maxTrainingQueue) * 100);
    }
    return fillPercentage;
  }, [isBarracks, maxTrainingQueue, trainingQueue, fillPercentage]);

  // Pulse animation for collectable buildings
  useEffect(() => {
    if ((isHighlighted || needsRepair) && !isAnimating) {
      setPulsing(true);

      const interval = setInterval(() => {
        setPulseScale((prev) => {
          // Oscillate between 0.95 and 1.05
          const newScale = prev + 0.005;
          return newScale > 1.05 ? 0.95 : newScale;
        });
      }, 50);

      return () => clearInterval(interval);
    } else {
      setPulsing(false);
      setPulseScale(1);
    }
  }, [isHighlighted, isAnimating, needsRepair]);

  // Handle click with animation
  const handleClick = (event: PIXI.FederatedPointerEvent) => {
    event.stopPropagation();

    if (!isAnimating && onClick) {
      setIsAnimating(true);
      // Trigger the provided onClick after animation starts
      onClick(event);

      // Reset after animation completes
      setTimeout(() => {
        setIsAnimating(false);
        setAnimationProgress(0);
        setVisible(true);
      }, 1500);
    }
  };

  // Animation effect
  useEffect(() => {
    let animationFrame: number;
    let startTime: number;

    if (isAnimating) {
      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const duration = 1000; // Animation duration in ms

        // Calculate progress (0 to 1)
        const progress = Math.min(elapsed / duration, 1);
        setAnimationProgress(progress);

        // Continue animation until complete
        if (progress < 1) {
          animationFrame = requestAnimationFrame(animate);
        } else {
          // We don't reset here - let the click handler reset
        }
      };

      animationFrame = requestAnimationFrame(animate);
    }

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [isAnimating]);

  // Draw the circular badge with pointer
  const drawMarker = useCallback(
    (g) => {
      g.clear();

      if (!visible) return;

      // Define badge dimensions
      const circleRadius = 60;
      const pointerWidth = 40;
      const pointerHeight = 30;

      // Calculate animation values
      const animScale = isAnimating
        ? 1 - animationProgress
        : pulsing
        ? pulseScale
        : 1;
      const animAlpha = isAnimating ? 1 - animationProgress : 1;

      if (animScale <= 0) return;

      // Apply animation scale
      g.scale.set(animScale, animScale);
      g.alpha = animAlpha;

      // Drop shadow (subtle black shadow)
      g.beginFill(0x3D372E, 0.3 * animAlpha);
      g.drawCircle(3, 3, circleRadius);
      // Draw pointer shadow
      g.moveTo(-pointerWidth / 2 + 3, circleRadius);
      g.lineTo(0 + 3, circleRadius + pointerHeight);
      g.lineTo(pointerWidth / 2 + 3, circleRadius);
      g.endFill();

      // Badge background color
      let backgroundColor = 0xf5f5f5;
      if (needsRepair) {
        backgroundColor = 0xff3333; // Red background for repair
      } else if (isHighlighted) {
        backgroundColor = 0xffe0a3; // Yellow for highlighted/collectible
      }

      // Badge background
      g.beginFill(backgroundColor, 0.4);
      g.drawCircle(0, 0, circleRadius);
      g.endFill();

      // Get the appropriate fill percentage based on building type
      const displayFillPercentage = isBarracks ? barracksQueuePercentage : fillPercentage;

      // Dynamic fill section based on capacity percentage
      const fillColor = needsRepair
        ? 0xff5722 // Orange for repair
        : isHighlighted
        ? 0x4caf50 // Bright green when highlighted
        : isBarracks
        ? displayFillPercentage > 80
          ? 0xf44336 // Red when barracks queue is almost full
          : displayFillPercentage > 60
          ? 0xff9800 // Orange when barracks queue is moderately full
          : displayFillPercentage > 40
          ? 0xffc107 // Yellow when barracks queue is half full
          : displayFillPercentage > 20
          ? 0x8bc34a // Light green when barracks queue is somewhat empty
          : 0x4caf50 // Green when barracks queue is mostly empty
        : displayFillPercentage > 80
        ? 0x4caf50 // Green when almost full (for non-barracks)
        : displayFillPercentage > 50
        ? 0xffc107 // Yellow/amber when half full
        : 0xff5722; // Orange/red when low

      g.beginFill(fillColor);

      // Handle fill percentage
      if (displayFillPercentage > 0 && !needsRepair) {
        if (displayFillPercentage >= 100) {
          // Fill the entire circle when 100%
          g.drawCircle(0, 0, circleRadius);
        } else {
          // Calculate the arc angle based on fill percentage (0-360 degrees)
          const fillAngle = Math.PI * 2 * (displayFillPercentage / 100);

          // Start at the bottom center of the circle (270 degrees)
          const startAngle = Math.PI / 2;

          // Draw arc clockwise
          g.arc(
            0,
            0,
            circleRadius,
            startAngle - fillAngle / 2,
            startAngle + fillAngle / 2
          );
          // Draw line to center
          g.lineTo(0, 0);
          // Close path
          g.closePath();
        }
      }
      g.endFill();

      // Pointer (green triangle at bottom)
      g.beginFill(0x3D372E);
      g.moveTo(-pointerWidth / 2, circleRadius);
      g.lineTo(0, circleRadius + pointerHeight);
      g.lineTo(pointerWidth / 2, circleRadius);
      g.endFill();

      // Badge border (dark brown)
      g.lineStyle(8, 0x3D372E);
      g.drawCircle(0, 0, circleRadius);

      // Draw the pointer outline
      g.lineStyle(5, 0x3D372E);
      g.moveTo(-pointerWidth / 2, circleRadius);
      g.lineTo(0, circleRadius + pointerHeight);
      g.lineTo(pointerWidth / 2, circleRadius);
      g.closePath();

      // When animating (collecting), add particle effects
      if (isAnimating && animationProgress > 0.2) {
        // Draw collection particles
        const particleCount = 8;
        for (let i = 0; i < particleCount; i++) {
          const angle = ((Math.PI * 2) / particleCount) * i;
          const dist = animationProgress * circleRadius * 2;
          const x = Math.cos(angle) * dist;
          const y = Math.sin(angle) * dist;
          const size = (1 - animationProgress) * 20;

          g.beginFill(0xffd700, 1 - animationProgress);
          g.drawCircle(x, y, size);
          g.endFill();
        }
      }
    },
    [
      isHighlighted,
      isAnimating,
      animationProgress,
      visible,
      pulsing,
      pulseScale,
      fillPercentage,
      needsRepair,
      isBarracks,
      barracksQueuePercentage
    ]
  );

  const markerHitArea = useMemo(() => new PIXI.Circle(0, 5, 70), []);
  const stopPointerPropagation = useCallback(
    (event: PIXI.FederatedPointerEvent) => event.stopPropagation(),
    []
  );

  return (
    <Container
      x={x}
      y={y}
      scale={scale}
      eventMode={onClick && !isAnimating ? "static" : "none"}
      hitArea={markerHitArea}
      pointerdown={onClick ? stopPointerPropagation : undefined}
      pointertap={onClick ? handleClick : undefined}
      cursor={onClick && !isAnimating ? "pointer" : "default"}
    >
      <Graphics draw={drawMarker} />

      {/* Building Icon - centered in the badge */}
      {visible && buildingIcon && (
        <Sprite
          image={ needBuild?buildIcon: needsRepair ? repairIcon : buildingIcon}
          x={0}
          y={0}
          width={isBarracks  && !needBuild? 90 : 80}
          height={isBarracks && !needBuild? 120 : 80}
          anchor={0.5}
          alpha={isAnimating ? 1 - animationProgress : 1}
          scale={isAnimating ? 1 - animationProgress : 1}
        />
      )}

      {isBarracks&& trainingQueue>0 && (
        <Sprite image={upgradeIcon} x={20} y={-60} width={45} height={60} />
      )}

      {/* Add "Collect" text for highlighted markers */}
      {/* {isHighlighted && !isAnimating && (
        <Container scale={2} y={-80}>
          <Graphics draw={(g) => {
            g.clear();
            g.beginFill(0x4CAF50, 0.8);
            g.drawRoundedRect(-50, 0, 100, 30, 8);
            g.endFill();
            g.lineStyle(2, 0x553311);
            g.drawRoundedRect(-50, 0, 100, 30, 8);
          }} />

          <Text
            text="COLLECT"
            x={0}
            y={15}
            anchor={0.5}
            style={collectTextStyle}
          />
        </Container>
      )} */}
    </Container>
  );
};

export default memo(BuildingMarker, (prevProps, nextProps) => {
  // Only re-render if these specific props change
  return (
    prevProps.x === nextProps.x &&
    prevProps.y === nextProps.y &&
    prevProps.scale === nextProps.scale &&
    prevProps.isHighlighted === nextProps.isHighlighted &&
    prevProps.fillPercentage === nextProps.fillPercentage &&
    prevProps.buildingIcon === nextProps.buildingIcon &&
    prevProps.needsRepair === nextProps.needsRepair &&
    prevProps.repairIcon === nextProps.repairIcon && 
    prevProps.buildIcon === nextProps.buildIcon &&
    prevProps.upgradeIcon === nextProps.upgradeIcon &&
    prevProps.buildingState === nextProps.buildingState &&
    prevProps.needBuild === nextProps.needBuild &&
    prevProps.trainingQueue === nextProps.trainingQueue &&
    prevProps.maxTrainingQueue === nextProps.maxTrainingQueue &&
    prevProps.isBarracks === nextProps.isBarracks &&
    prevProps.onClick === nextProps.onClick
  );
});
